import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Download as DownloadIcon,
  GetApp as ExportIcon,
} from '@mui/icons-material';
import { CustomerSearchFilters } from '../types/customer';
import apiService from '../services/api';

const ExportPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  
  // Export filters
  const [searchQuery, setSearchQuery] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [status, setStatus] = useState('');
  const [hasEmail, setHasEmail] = useState<boolean | undefined>(undefined);
  const [hasMobile, setHasMobile] = useState<boolean | undefined>(undefined);

  const handleExport = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Build filters
      const filters: CustomerSearchFilters = {};
      
      if (searchQuery.trim()) filters.q = searchQuery.trim();
      if (serviceType) filters.service_type = serviceType as any;
      if (status) filters.status = status as any;
      if (hasEmail !== undefined) filters.has_email = hasEmail;
      if (hasMobile !== undefined) filters.has_mobile = hasMobile;

      // Export customers
      const csvBlob = await apiService.exportCustomersCSV(filters);
      
      // Create download link
      const url = window.URL.createObjectURL(csvBlob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
      const filterSuffix = Object.keys(filters).length > 0 ? '_filtered' : '';
      link.download = `customers_export_${timestamp}${filterSuffix}.csv`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSuccess('Customer data exported successfully!');
    } catch (error: any) {
      console.error('Export failed:', error);
      setError(error.response?.data?.detail || 'Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setServiceType('');
    setStatus('');
    setHasEmail(undefined);
    setHasMobile(undefined);
    setError('');
    setSuccess('');
  };

  const hasFilters = searchQuery || serviceType || status || hasEmail !== undefined || hasMobile !== undefined;

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Export Customers
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        Export customer data to CSV format with optional filtering
      </Typography>

      {/* Export Options Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Export Options
          </Typography>
          
          <Grid container spacing={2}>
            {/* Search Query */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search Query"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                helperText="Search in customer names, emails, and service IDs"
                disabled={loading}
              />
            </Grid>

            {/* Service Type Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                select
                label="Service Type"
                value={serviceType}
                onChange={(e) => setServiceType(e.target.value)}
                disabled={loading}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="nbn">NBN</MenuItem>
                <MenuItem value="mobile">Mobile</MenuItem>
                <MenuItem value="voice">Voice</MenuItem>
                <MenuItem value="data">Data</MenuItem>
              </TextField>
            </Grid>

            {/* Status Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                select
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                disabled={loading}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
              </TextField>
            </Grid>

            {/* Contact Information Filters */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Contact Information Filters
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={hasEmail === true}
                        indeterminate={hasEmail === undefined}
                        onChange={(e) => {
                          if (hasEmail === true) {
                            setHasEmail(false);
                          } else if (hasEmail === false) {
                            setHasEmail(undefined);
                          } else {
                            setHasEmail(true);
                          }
                        }}
                        disabled={loading}
                      />
                    }
                    label={
                      hasEmail === true 
                        ? "Has email address" 
                        : hasEmail === false 
                        ? "No email address"
                        : "Email address (any)"
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={hasMobile === true}
                        indeterminate={hasMobile === undefined}
                        onChange={(e) => {
                          if (hasMobile === true) {
                            setHasMobile(false);
                          } else if (hasMobile === false) {
                            setHasMobile(undefined);
                          } else {
                            setHasMobile(true);
                          }
                        }}
                        disabled={loading}
                      />
                    }
                    label={
                      hasMobile === true 
                        ? "Has mobile number" 
                        : hasMobile === false 
                        ? "No mobile number"
                        : "Mobile number (any)"
                    }
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <Button
                variant="contained"
                size="large"
                startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
                onClick={handleExport}
                disabled={loading}
                fullWidth
              >
                {loading ? 'Exporting...' : 'Export to CSV'}
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                size="large"
                onClick={clearFilters}
                disabled={loading}
                fullWidth
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>

          {hasFilters && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Active Filters:</strong> Only customers matching your filter criteria will be exported.
                {!hasFilters && ' All customers will be exported.'}
              </Typography>
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Export Information */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Export Information
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            The exported CSV file will contain all customer information including:
          </Typography>
          <ul>
            <li>Service ID and Member ID</li>
            <li>Organisation and contact names</li>
            <li>Contact information (email and mobile)</li>
            <li>Service type and address</li>
            <li>Customer status</li>
            <li>Notification preferences</li>
            <li>Creation and update timestamps</li>
          </ul>
          <Typography variant="body2" color="text.secondary">
            The exported data can be used for backup purposes or imported into other systems.
            You can also modify the CSV and re-import it using the Import Customers feature.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ExportPage;
